import { Dayjs } from "dayjs"
import { IconStringWithModifiers } from "@/types/Icon"

export type SoDataTableCompareFunction<T = any> = (a: T, b: T) => number

export enum SoDataTableColumnType {
  DATE = "DATE",
  DATE_TIME = "DATE_TIME",
  DATE_PERIOD = "DATE_PERIOD",
  MONEY = "MONEY",
  MONEY_COLORED = "MONEY_COLORED",
  MONEY_ARROW = "MONEY_ARROW",
  CHIP = "CHIP",
  CHIPS = "CHIPS",
  STATUS = "STATUS",
  STATUSES = "STATUSES",
  YES_OR_NO = "YES_OR_NO",
  COPY_TO_CLIPBOARD = "COPY_TO_CLIPBOARD",
  LINK = "LINK",
  EXTERNAL_LINK = "EXTERNAL_LINK",
  ICON = "ICON",
  LIST = "LIST",
  PERCENTAGE = "PERCENTAGE",
}

export enum FilterType {
  RADIO = "RADIO",
  CHECKBOX = "CHECKBOX",
  DATE_RANGE = "DATE_RANGE",
  DATE_TIME_RANGE = "DATE_TIME_RANGE",
  MONEY_RANGE = "MONEY_RANGE",
  NUMBER_RANGE = "NUMBER_RANGE",
}

export interface SoDataTableHeader<TKey = string, TSortKey = string> {
  key: TKey
  title: string
  type?: SoDataTableColumnType
  colspan?: number
  rowspan?: number
  align?: "start" | "end"
  sortable?: boolean
  fallbackValue?: string
  isVisible?: boolean
  sort?: SoDataTableCompareFunction
  sortKey?: TSortKey
  icon?:
    | IconStringWithModifiers
    | {
        name: IconStringWithModifiers
        tooltip?: string
      }
}

export type SoDataTableCustomComponent<T = string> = {
  value: string
  props?: {
    [key: string]: T
  }
}

export type SoDataTableDatePeriod = {
  datePeriod: {
    from: Dayjs
    to: Dayjs
  }
}

export type SoDataTableItem = {
  [key: string]: unknown
}

export type SoDataTableSortItem = {
  key: string
  order?: boolean | "asc" | "desc"
}

export type SoDataTableOrderBy = {
  [key: string]: "asc" | "desc"
}

export type SoDataTablePaginationOption = {
  title: string
  value: number
}

export type SoDataTableSelectableItems = (item: SoDataTableItem) => boolean

// Data Table Filters

interface Filter {
  label: string
  filter?: (filters: SoDataTableFilters, item: SoDataTableItem) => boolean // client filtering only
}

export interface FilterRadio<T = string> extends Filter {
  type: FilterType.RADIO
  value: T | null
  options: Record<string, string>
  expandable?: boolean
}

export interface FilterCheckbox<T = string> extends Filter {
  type: FilterType.CHECKBOX
  value: Array<T>
  options: Record<string, string>
  expandable?: boolean
  truncateText?: boolean
}

export interface FilterDateRange extends Filter {
  type: FilterType.DATE_RANGE
  value: {
    from: Date | null
    to: Date | null
  }
  fromLabel?: string
  toLabel?: string
  expandable?: boolean
}

export interface FilterDateTimeRange extends Filter {
  type: FilterType.DATE_TIME_RANGE
  value: Date[] | null
  expandable?: boolean
}

export interface FilterMoneyRange extends Filter {
  type: FilterType.MONEY_RANGE
  value: {
    min: number | null
    max: number | null
  }
  minLabel?: string
  maxLabel?: string
  expandable?: boolean
}

export interface FilterNumberRange extends Filter {
  type: FilterType.NUMBER_RANGE
  value: {
    min: number | null
    max: number | null
  }
  expandable?: boolean
}

export type SoDataTableFilter =
  | FilterRadio
  | FilterCheckbox
  | FilterDateRange
  | FilterDateTimeRange
  | FilterMoneyRange
  | FilterNumberRange

export interface SoDataTableFilters {
  [filterName: string]: SoDataTableFilter
}
